'use client'

import { cn } from '@/_lib/utils'
import { useOrderFlow } from '@/app/[locale]/order/_context/order-flow-context'
import { useCartPrices } from '@cart/_hooks/use-cart-prices'
import { listPriceWhenDiscounted } from '@cart/_utils/cart-price-aggregates'
import { cartProductKey } from '@cart/_utils/cart-product-key'
import { familyModuleCount } from '@cart/_utils/family-module-count'
import { isAllAnnualProgramFamily } from '@cart/_utils/is-all-annual-program-family'


import { CartItemDisplay } from './a-cart-item-lines'
import { ProductNameRow } from './a-cart-ui-primitives'

type AOrderMobileCartSummaryProps = {
	className?: string
	/** Patient step: hide price emphasis so the slot label stays readable. @default false */
	patientStepHeaderMinimal?: boolean
}

/**
 * Compact cart strip rendered inside the mobile order header (via portal).
 */
export function AOrderMobileCartSummary({
	className,
	patientStepHeaderMinimal = false
}: AOrderMobileCartSummaryProps) {
	const { cartProducts } = useOrderFlow()
	const { reportPrice, pricesLoaded, totalAll, savingsAll } = useCartPrices(cartProducts)

	const isAllFamilyPrograms = isAllAnnualProgramFamily(cartProducts)
	const totalFamilyModules = isAllFamilyPrograms ? familyModuleCount(cartProducts) : 0

	if (cartProducts.length === 0) return null

	if (isAllFamilyPrograms) {
		const n = cartProducts.length
		const aggregateOldPrice = listPriceWhenDiscounted(pricesLoaded, totalAll, savingsAll)
		return (
			<div className={cn(className)}>
				<div className='hidden' aria-hidden>
					{cartProducts.map((product, idx) => {
						const key = cartProductKey(product, idx)
						return (
							<CartItemDisplay
								key={key}
								product={product}
								variant='compact'
								onPriceReport={info => reportPrice(key, info)}
							/>
						)
					})}
				</div>

				<div className='min-w-0 flex-1 space-y-3'>
					<div className='border-t border-medante-purple-dark/10 pt-3 first:mt-0 first:border-t-0 first:pt-0'>
						<div className='space-y-0.5'>
							<div
								className={cn(
									patientStepHeaderMinimal &&
										'[&_.flex.shrink-0]:pointer-events-none [&_.flex.shrink-0]:select-none [&_.flex.shrink-0]:opacity-0'
								)}
							>
								{pricesLoaded ? (
									<ProductNameRow
										name={`${n}× Ročný program`}
										price={totalAll}
										oldPrice={aggregateOldPrice}
										variant='compact'
									/>
								) : (
									<div className='flex flex-wrap items-baseline gap-x-2'>
										<p className='min-w-0 flex-1 line-clamp-2 text-xs font-medium leading-snug text-medante-purple-dark'>
											{n}× Ročný program
										</p>
										<div
											className={cn(
												'flex shrink-0 items-baseline gap-1.5',
												patientStepHeaderMinimal &&
													'pointer-events-none select-none opacity-0'
											)}
											aria-hidden={patientStepHeaderMinimal}
										>
											<div className='h-3 w-14 animate-pulse rounded bg-medante-purple-dark/10' />
										</div>
									</div>
								)}
							</div>
							{totalFamilyModules > 0 && (
								<p className='text-xs font-medium text-medante-purple-dark/60'>
									Vrátane {totalFamilyModules}{' '}
									{totalFamilyModules === 1 ? 'modulu' : 'modulov'}
								</p>
							)}
							{!patientStepHeaderMinimal && (
								<p className='text-xs font-medium text-medante-purple-dark/60'>
									Vrátane 10% zľavy na rodinné ročné programy
								</p>
							)}
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className={cn(className)}>
			<div className='min-w-0 flex-1 space-y-3'>
				{cartProducts.map((product, idx) => {
					const key = cartProductKey(product, idx)
					return (
						<div
							key={key}
							className='border-t border-medante-purple-dark/10 pt-3 first:mt-0 first:border-t-0 first:pt-0'
						>
							<CartItemDisplay product={product} variant='compact' />
						</div>
					)
				})}
			</div>
		</div>
	)
}
